import React, { useEffect, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { MasterclassVideos } from "@/components/MasterclassVideos";
import { TrustBadgesStrip } from "@/components/TrustBadgesStrip";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import CPDLogger from "@/components/CPDLogger";
import { AmbassadorDiscount, AMBASSADOR_COURSES } from "@/components/AmbassadorDiscount";
import { ProgrammeFaq } from "@/components/ProgrammeFaq";
import { creditLabel } from "@/data/credits";
import {
  PlayCircle, FileQuestion, Users, Clock, Star, Radio,
  Lock, Target, GraduationCap, CheckSquare, ClipboardCheck, Calendar, Award, ExternalLink, ShieldCheck, FileText, Scale, Trophy, Route,
  Send, MessageCircle, BarChart3, TrendingUp, Linkedin,
} from "lucide-react";

const DELIVERY_META = {
  exam_only: { label: "Exam only · partner portal", icon: ExternalLink, cls: "bg-[var(--navy)] text-[var(--gold-soft)] border-[var(--navy)]" },
  direct_assessment: { label: "Chartered route · direct assessment", icon: GraduationCap, cls: "bg-[var(--gold-deep)] text-white border-[var(--gold-deep)]" },
};

function InfoBlock({ icon: Icon, title, children }) {
  if (!children || (Array.isArray(children) && children.length === 0)) return null;
  return (
    <div className="card-warm p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="text-[var(--gold-deep)]"/>
        <h3 className="eyebrow">{title}</h3>
      </div>
      <div className="text-sm text-zinc-800 leading-relaxed">{children}</div>
    </div>
  );
}

export default function CourseDetailPage() {
  const { id: param } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const confirmIntentId = searchParams.get("confirm");
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const id = course?.id;
  useSeo({
    title: course ? `${course.code && !course.title.includes(course.code.split("-")[0]) ? course.code + " - " : ""}${course.title}` : null,
    description: course?.subtitle || course?.description,
    path: `/courses/${course?.slug || param}`,
    image: course?.slug ? `${process.env.REACT_APP_BACKEND_URL}/api/og/courses/${course.slug}.jpg` : null,
    jsonLd: course && {
      "@context": "https://schema.org", "@type": "Course", name: course.title,
      description: (course.subtitle || course.description || "").slice(0, 300),
      courseCode: course.code || undefined,
      provider: { "@type": "EducationalOrganization", name: "Converged Security Institute", sameAs: "https://convergedsecurity.courses" },
      ...(course.price ? { offers: { "@type": "Offer", price: course.price, priceCurrency: "EUR", category: "Paid" } } : {}),
    },
  });
  const [enrolled, setEnrolled] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [enrolmentRecord, setEnrolmentRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sumUpStep, setSumUpStep] = useState(null); // {intent_id, payment_link_url}
  const [sumupRef, setSumupRef] = useState("");
  const [sumupProof, setSumupProof] = useState("");
  const [proofUploading, setProofUploading] = useState(false);
  const [tierPicker, setTierPicker] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null); // index

  const load = async () => {
    const { data } = await api.get(`/courses/${param}`);
    setCourse(data);
    if (data.slug && param !== data.slug) nav(`/courses/${data.slug}${window.location.search}`, { replace: true });
  };
  const checkEnrolled = async () => {
    if (!user) { setEnrolled(false); setEnrolmentRecord(null); return; }
    try {
      const { data } = await api.get("/enrollments/me");
      const match = data.find((e) => e.course_id === id);
      setEnrolled(!!match);
      setEnrolmentRecord(match || null);
    } catch { setEnrolled(false); setEnrolmentRecord(null); }
  };

  useEffect(() => { load(); }, [param]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { checkEnrolled(); }, [id, user]);

  // If user arrived from /dashboard "I paid - add proof" link, open the confirm modal for that intent
  useEffect(() => {
    if (!confirmIntentId || !user) return;
    (async () => {
      try {
        const { data } = await api.get("/payments/intents/me");
        const match = (data || []).find((i) => i.id === confirmIntentId);
        if (match && (match.status === "awaiting_payment" || match.status === "pending_review")) {
          setSumUpStep({
            intent_id: match.id,
            payment_link_url: match.payment_link_url,
            amount: match.amount,
            tier_label: match.tier_label,
          });
          setSumupRef(match.sumup_reference || "");
          setSumupProof(match.proof_url || "");
        }
      } catch { /* no-op */ }
    })();
  }, [confirmIntentId, user]);

  if (!course) return <div className="min-h-[40vh] grid place-items-center text-sm text-zinc-500">Loading course…</div>;

  const totalHours = course.learning_hours || 0;
  const isExamOnly = course.delivery_type === "exam_only" || course.delivery_type === "direct_assessment";
  const deliveryMeta = DELIVERY_META[course.delivery_type];

  const handleEnroll = async () => {
    if (!user) return nav("/login");
    // If tier-based pricing, open tier picker first
    if (course.pricing_tiers?.length > 0 && !enrolled) {
      setSelectedTier(null);
      setTierPicker(true);
      return;
    }
    setLoading(true);
    try {
      if (course.price > 0) {
        if (!course.payment_link_url) {
          toast.error("Payment is not yet configured for this programme. Please contact info@convergedsecurity.es.");
          return;
        }
        const { data } = await api.post("/payments/sumup/intent", { course_id: course.id, ...(discount ? { discount_code: discount.code } : {}) });
        window.open(data.payment_link_url, "_blank", "noopener,noreferrer");
        setSumUpStep({ intent_id: data.intent_id, payment_link_url: data.payment_link_url, amount: data.amount, tier_label: data.tier_label });
      } else {
        await api.post(`/enrollments/${course.id}`);
        toast.success("You're in! Opening your exam…");
        setEnrolled(true);
        nav(`/exams/take/${course.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const payForTier = async (tierIndex) => {
    if (!user) return nav("/login");
    const tier = course.pricing_tiers?.[tierIndex];
    if (!tier) return;
    if (!tier.payment_link_url) {
      toast.info(`The ${tier.tier} tier is not yet available online. Please contact info@convergedsecurity.es to enrol.`);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/sumup/intent", { course_id: course.id, tier_index: tierIndex });
      window.open(data.payment_link_url, "_blank", "noopener,noreferrer");
      setTierPicker(false);
      setSumUpStep({ intent_id: data.intent_id, payment_link_url: data.payment_link_url, amount: data.amount, tier_label: data.tier_label });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const confirmSumUp = async (e) => {
    e.preventDefault();
    if (!sumUpStep) return;
    if (!sumupRef.trim() && !sumupProof) {
      toast.error("Please enter the SumUp reference or upload a photo of your receipt.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/payments/sumup/confirm", {
        intent_id: sumUpStep.intent_id,
        reference: sumupRef.trim(),
        proof_url: sumupProof || "",
      });
      toast.success("Thank you! CSI will verify your payment and activate your enrolment shortly.");
      setSumUpStep(null);
      setSumupRef("");
      setSumupProof("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|gif|webp)$/.test(file.type)) { toast.error("Please upload a PNG, JPG, GIF or WEBP image"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image is too large (max 8 MB)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setProofUploading(true);
    try {
      const { data } = await api.post("/payments/upload-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSumupProof(data.url);
      toast.success("Receipt uploaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setProofUploading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-8 min-w-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="eyebrow">{course.category}</span>
          {deliveryMeta && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${deliveryMeta.cls}`}>
              <deliveryMeta.icon size={11}/> {deliveryMeta.label}
            </span>
          )}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight">{course.title}</h1>
        <p className="mt-4 text-zinc-700">{course.description}</p>
        <div className="mt-6 flex flex-wrap gap-4 text-sm text-zinc-700">
          <span className="inline-flex items-center gap-1.5"><Star size={14} className="text-[var(--gold-deep)]"/> {course.rating || 4.8}</span>
          <span className="inline-flex items-center gap-1.5"><Users size={14}/> {course.enrolled_count || 0} learners</span>
          {!isExamOnly && <span className="inline-flex items-center gap-1.5"><Clock size={14}/> {totalHours}h total {creditLabel(course.code) ? `· ${creditLabel(course.code)}` : ""}</span>}
          {course.is_live && (
            <span className="inline-flex items-center gap-1.5 text-red-600 font-bold"><Radio size={14}/> Live cohort</span>
          )}
        </div>

        <div className="mt-8 rounded-3xl overflow-hidden border border-[var(--border-warm)]"
             style={{ background: course.cover_url?.startsWith("/badges/")
               ? "radial-gradient(ellipse at center, #22365F 0%, #0F2A54 55%, #091B38 100%)"
               : "transparent" }}>
          {course.cover_url?.startsWith("/badges/") ? (
            <img src={course.cover_url} alt={course.title} className="w-full h-72 object-contain p-8 drop-shadow-[0_8px_30px_rgba(201,165,87,0.35)]"/>
          ) : (
            <img src={course.cover_url} alt={course.title} className="w-full h-72 object-cover"/>
          )}
        </div>

        {/* Enriched content */}
        {(course.overview || course.who_for || course.outcomes || course.assessment || course.delivery || course.prerequisite) && (
          <section className="mt-10 space-y-5">
            {course.overview && (
              <div>
                <h2 className="font-display text-2xl mb-2">Overview</h2>
                <p className="text-zinc-800 leading-relaxed">{course.overview}</p>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {course.who_for?.length > 0 && (
                <InfoBlock icon={Target} title="Who it's for">
                  <ul className="space-y-1.5">
                    {course.who_for.map((w, i) => (
                      <li key={i} className="flex gap-2"><CheckSquare size={14} className="shrink-0 mt-0.5 text-[var(--gold-deep)]"/><span>{w}</span></li>
                    ))}
                  </ul>
                </InfoBlock>
              )}
              {course.outcomes?.length > 0 && (
                <InfoBlock icon={Award} title="Learning outcomes">
                  <ul className="space-y-1.5">
                    {course.outcomes.map((o, i) => (
                      <li key={i} className="flex gap-2"><CheckSquare size={14} className="shrink-0 mt-0.5 text-[var(--gold-deep)]"/><span>{o}</span></li>
                    ))}
                  </ul>
                </InfoBlock>
              )}
              {course.assessment && (
                <InfoBlock icon={ClipboardCheck} title="Assessment & Award">{course.assessment}</InfoBlock>
              )}
              {course.delivery && (
                <InfoBlock icon={Calendar} title="Delivery format">{course.delivery}</InfoBlock>
              )}
              {course.prerequisite && (
                <InfoBlock icon={ShieldCheck} title="Prerequisite">{course.prerequisite}</InfoBlock>
              )}
              {course.validity && (
                <InfoBlock icon={Award} title="Validity & CPD">{course.validity}</InfoBlock>
              )}
              {course.membership_grade && (
                <InfoBlock icon={ShieldCheck} title="Membership grade on award">{course.membership_grade}</InfoBlock>
              )}
              {(course.credits || course.learning_hours) && (
                <InfoBlock icon={Award} title="Qualification value">
                  {creditLabel(course.code)}
                  {course.credits && course.learning_hours ? " · " : ""}
                  {course.learning_hours ? `${course.learning_hours} Indicative Learning Hours` : ""}
                </InfoBlock>
              )}
            </div>

            {course.competencies?.length > 0 && (
              <div>
                <h2 className="font-display text-2xl mb-3 mt-2">Competencies developed</h2>
                <div className="flex flex-wrap gap-2">
                  {course.competencies.map((c, i) => (
                    <span key={i}
                          className="inline-flex items-center rounded-full border border-[var(--border-warm)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--navy)]"
                          data-testid={`competency-${i}`}>
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* PGDCSL-style pricing / award / services (shown when provided) */}
        {course.award_code && (
          <CPDLogger designationCode={course.award_code}/>
        )}

        {course.entry_routes?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Entry Routes</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {course.entry_routes.map((r, i) => (
                <div key={i} className="card-warm p-5" data-testid={`entry-route-${i}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Route size={16} className="text-[var(--gold-deep)]"/>
                    <h3 className="font-display text-lg leading-tight">{r.route}</h3>
                  </div>
                  <p className="text-sm text-zinc-700 leading-relaxed">{r.detail}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {course.classification?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Award Classification</h2>
            <div className="grid md:grid-cols-3 gap-3">
              {course.classification.map((c, i) => (
                <div key={i} className="card-warm p-5 text-center" data-testid={`classification-${c.grade.toLowerCase()}`}>
                  <Trophy size={18} className="mx-auto text-[var(--gold-deep)]"/>
                  <div className="font-display text-xl mt-2">{c.grade}</div>
                  <div className="font-display text-2xl text-[var(--gold-deep)] mt-1">{c.range}</div>
                  <div className="text-xs text-zinc-600 mt-1">{c.note}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {course.component_weightings?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Component Weightings</h2>
            <div className="rounded-2xl border border-[var(--border-warm)] bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--muted-surface)]">
                  <tr className="text-left">
                    <th className="py-3 px-4 font-semibold text-[var(--navy)]">Component</th>
                    <th className="py-3 px-4 font-semibold text-[var(--navy)] text-right">Weighted Contribution</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-warm)]">
                  {course.component_weightings.map((w, i) => (
                    <tr key={i} data-testid={`weighting-${i}`}>
                      <td className="py-2.5 px-4 flex items-center gap-2"><Scale size={14} className="text-[var(--gold-deep)]"/>{w.component}</td>
                      <td className="py-2.5 px-4 text-right font-display text-base text-[var(--gold-deep)]">{w.weight}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {course.manual_facts?.length > 0 && (
              <ul className="mt-4 grid md:grid-cols-2 gap-2 text-xs text-zinc-700">
                {course.manual_facts.map((f, i) => (
                  <li key={i} className="flex gap-2 card-warm p-3" data-testid={`manual-fact-${i}`}>
                    <FileText size={13} className="text-[var(--gold-deep)] mt-0.5 shrink-0"/><span>{f}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-5 rounded-2xl border border-[var(--border-warm)] bg-[var(--muted-surface)] p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <div className="eyebrow text-[var(--gold-deep)]">PGDCSL Governance</div>
                <div className="font-semibold text-[var(--navy)]">Programme &amp; Governance Manual · v1.0</div>
              </div>
              <div className="flex gap-2">
                <Link to="/policies" className="chip inline-flex items-center gap-1" data-testid="goto-policies">Browse policies</Link>
                <a href="/documents/pgdcsl-programme-governance-manual.pdf" target="_blank" rel="noreferrer"
                   className="btn-terracotta inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold"
                   data-testid="download-manual-pdf">
                  <FileText size={13}/> Download Manual (PDF)
                </a>
              </div>
            </div>
          </section>
        )}

        {course.pricing_tiers?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Pricing Structure</h2>
            <div className="overflow-x-auto rounded-2xl border border-[var(--border-warm)] bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[var(--muted-surface)]">
                  <tr className="text-left">
                    <th className="py-3 px-4 font-semibold text-[var(--navy)]">Tier</th>
                    <th className="py-3 px-4 font-semibold text-[var(--navy)]">Eligibility</th>
                    <th className="py-3 px-4 font-semibold text-[var(--navy)] text-right">Price (€)</th>
                    <th className="py-3 px-4 font-semibold text-[var(--navy)]">Includes</th>
                    <th className="py-3 px-4 font-semibold text-[var(--navy)] text-right">Enrol</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-warm)]">
                  {course.pricing_tiers.map((t, i) => (
                    <tr key={i} data-testid={`pgdcsl-tier-${i}`} className={i === 0 ? "bg-[var(--muted-surface)]/40" : ""}>
                      <td className="py-3 px-4 font-semibold">
                        {t.tier}
                        {i === 0 && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[var(--gold-deep)]">Founding</span>}
                      </td>
                      <td className="py-3 px-4 text-zinc-700">{t.eligibility}</td>
                      <td className="py-3 px-4 text-right font-display text-lg text-[var(--gold-deep)]">€{t.price.toLocaleString()}</td>
                      <td className="py-3 px-4 text-zinc-700">{t.includes}</td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        {enrolled ? (
                          <span className="text-xs text-zinc-500">Enrolled</span>
                        ) : t.payment_link_url ? (
                          <button
                            onClick={() => payForTier(i)}
                            disabled={loading}
                            className="btn-terracotta inline-flex items-center justify-center px-3 py-1.5 rounded-full text-xs font-semibold disabled:opacity-70"
                            data-testid={`pgdcsl-tier-pay-${i}`}
                          >Enrol & Pay</button>
                        ) : (
                          <a href="mailto:info@convergedsecurity.es?subject=PGDCSL enrolment enquiry"
                             className="chip inline-flex items-center justify-center px-3 py-1.5 text-xs"
                             data-testid={`pgdcsl-tier-contact-${i}`}>Contact us</a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {course.award_package && (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Included in Every PGDCSL Award Package</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(course.award_package).map(([group, items]) => (
                <div key={group} className="card-warm p-5" data-testid={`award-${group.replace(/\s/g,'-').toLowerCase()}`}>
                  <div className="eyebrow mb-2">{group}</div>
                  <ul className="space-y-1.5 text-sm text-zinc-800">
                    {items.map((it, i) => (
                      <li key={i} className="flex gap-2"><CheckSquare size={14} className="shrink-0 mt-0.5 text-[var(--gold-deep)]"/><span>{it}</span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {course.optional_services?.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-2xl mb-4">Optional Services</h2>
            <div className="rounded-2xl border border-[var(--border-warm)] bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[var(--muted-surface)]">
                  <tr className="text-left">
                    <th className="py-3 px-4 font-semibold text-[var(--navy)]">Service</th>
                    <th className="py-3 px-4 font-semibold text-[var(--navy)] text-right">Fee</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-warm)]">
                  {course.optional_services.map((s, i) => (
                    <tr key={i} data-testid={`optional-service-${i}`}>
                      <td className="py-2.5 px-4">{s.service}</td>
                      <td className="py-2.5 px-4 text-right font-semibold text-[var(--navy)]">{s.fee}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Unified course panel: Launch exam · Upcoming live sessions · Benchmark · Contact */}
        <CourseActionPanel course={course} enrolled={enrolled} user={user} onRepay={() => {
          if (course.pricing_tiers?.length > 0) { setSelectedTier(null); setTierPicker(true); } else handleEnroll();
        }}/>
      </div>

      <aside className="lg:col-span-4">
        <div className="card-warm p-6 sticky top-24">
          <div className="eyebrow mb-1">Enrol</div>
          <div className="font-display text-3xl">
            {discount && !enrolled ? <span data-testid="course-discounted-price"><s className="text-zinc-400 text-xl mr-2">€{course.price}</s>€{discount.discounted_price}</span>
              : course.pricing_tiers?.length > 0 ? `from €${course.pricing_tiers[0].price.toLocaleString()}` : (course.price > 0 ? `€${course.price}` : "Enquire")}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {course.pricing_tiers?.length > 0 ? `${course.pricing_tiers[0].tier} tier · ${course.pricing_tiers.length} pricing tiers available` : (isExamOnly ? "Partner portal · verifiable credential" : "Live cohort · verifiable credential · CPD-aligned")}
          </div>
          {!enrolled && AMBASSADOR_COURSES.includes(course.code) && <AmbassadorDiscount course={course} discount={discount} onChange={setDiscount}/>}
          {course.slug && (
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/api/share/courses/${course.slug}`)}`}
               target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--navy)] hover:underline" data-testid="course-share-linkedin">
              <Linkedin size={13}/> Share this programme on LinkedIn
            </a>
          )}
          {enrolled ? (
            <Link to={`/exams/take/${course.id}`}
               className="btn-ink w-full mt-5 py-3 rounded-full text-sm font-semibold text-center block inline-flex items-center justify-center gap-2"
               data-testid="aside-launch-exam">
              <ExternalLink size={14}/> Launch exam
            </Link>
          ) : (
            <button
              onClick={handleEnroll}
              disabled={loading}
              className="btn-terracotta w-full mt-5 py-3 rounded-full text-sm font-semibold disabled:opacity-70"
              data-testid="enroll-course-btn"
            >
              {loading ? "Working…" : course.price > 0 ? (isExamOnly ? "Pay & unlock exam" : "Enrol & Pay") : "Enquire / Enrol"}
            </button>
          )}

          {course.min_experience_years && (
            <div className="mt-4 text-xs rounded-xl border border-[var(--gold-deep)]/40 bg-[var(--muted-surface)] p-3">
              <b className="text-[var(--gold-deep)]">Chartered eligibility</b>
              <div className="text-zinc-700 mt-1">Minimum {course.min_experience_years} years senior security leadership experience required. All candidates subject to CSI review.</div>
            </div>
          )}

          {course.instructor && !isExamOnly && (
            <div className="mt-6 pt-6 border-t border-[var(--border-warm)]">
              <div className="eyebrow mb-2">Instructor</div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[var(--muted-surface)] grid place-items-center font-bold">
                  {course.instructor.name?.[0]}
                </div>
                <div>
                  <div className="font-semibold text-sm">{course.instructor.name}</div>
                  <div className="text-xs text-zinc-500 line-clamp-2">{course.instructor.bio}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>

      <TrustBadgesStrip className="lg:col-span-12 min-w-0"/>
      <ProgrammeFaq course={course} className="lg:col-span-12 min-w-0"/>
      <MasterclassVideos className="lg:col-span-12 min-w-0" title="Masterclasses from CSI faculty"/>
      {tierPicker && course.pricing_tiers?.length > 0 && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setTierPicker(false)}>
          <div onClick={(e)=>e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-lg space-y-3" data-testid="pgdcsl-tier-picker">
            <div className="eyebrow">PGDCSL · Choose your tier</div>
            <h3 className="font-display text-2xl">Select the pricing tier that applies to you</h3>
            <p className="text-sm text-zinc-600">Select a tier below. You'll be redirected to SumUp to complete payment for that tier only.</p>
            <div className="divide-y divide-[var(--border-warm)] border border-[var(--border-warm)] rounded-xl overflow-hidden mt-2">
              {course.pricing_tiers.map((t, i) => {
                const active = selectedTier === i;
                const available = !!t.payment_link_url;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedTier(i)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 ${active ? "bg-[var(--muted-surface)]" : "bg-white hover:bg-[var(--muted-surface)]/60"}`}
                    data-testid={`tier-picker-row-${i}`}
                  >
                    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${active ? "border-[var(--gold-deep)] bg-[var(--gold-deep)]" : "border-zinc-400"}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-semibold text-sm">{t.tier}</div>
                        <div className="font-display text-[var(--gold-deep)]">€{t.price.toLocaleString()}</div>
                      </div>
                      <div className="text-xs text-zinc-600 mt-0.5">{t.eligibility}</div>
                      {!available && <div className="text-[11px] text-zinc-500 mt-1 italic">Online payment not yet enabled - contact CSI to enrol</div>}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" className="chip flex-1" onClick={()=>setTierPicker(false)} data-testid="tier-picker-cancel">Cancel</button>
              <button
                type="button"
                className="btn-terracotta flex-1 py-2 rounded-full text-sm font-semibold disabled:opacity-60"
                disabled={selectedTier === null || loading}
                onClick={() => payForTier(selectedTier)}
                data-testid="tier-picker-continue"
              >{loading ? "Working…" : "Continue to payment"}</button>
            </div>
          </div>
        </div>
      )}

      {sumUpStep && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setSumUpStep(null)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={confirmSumUp} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" data-testid="sumup-confirm-modal">
            <div className="eyebrow">SumUp payment · €{sumUpStep.amount}{sumUpStep.tier_label ? ` · ${sumUpStep.tier_label}` : ""}</div>
            <h3 className="font-display text-2xl">Confirm your payment</h3>
            <p className="text-sm text-zinc-700">The SumUp payment page opened in a new tab. Once you've paid, submit either the <b>SumUp transaction reference</b> or a <b>photo of your receipt</b> - CSI will verify and activate your enrolment within one business day.</p>
            <a href={sumUpStep.payment_link_url} target="_blank" rel="noreferrer" className="chip inline-flex items-center gap-1 text-xs" data-testid="sumup-reopen-link">↗ Reopen SumUp page</a>

            <label className="block text-xs font-semibold mt-3">SumUp transaction reference</label>
            <input className="w-full rounded-xl border border-[var(--border-warm)] px-3 py-2 text-sm outline-none focus:border-[var(--navy)]"
                   placeholder="e.g. T1234A567" value={sumupRef} onChange={(e)=>setSumupRef(e.target.value)} minLength={3}
                   data-testid="sumup-ref-input"/>

            <div className="relative py-1">
              <div className="absolute inset-y-1/2 left-0 right-0 border-t border-[var(--border-warm)]"></div>
              <div className="relative text-center">
                <span className="bg-white px-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500">or</span>
              </div>
            </div>

            <label className="block text-xs font-semibold">Upload a photo of your receipt</label>
            {sumupProof ? (
              <div className="relative inline-block">
                <img src={sumupProof} alt="Receipt" className="h-32 w-auto rounded-xl object-cover border border-[var(--border-warm)]" data-testid="sumup-proof-preview"/>
                <button type="button" onClick={()=>setSumupProof("")} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[var(--terracotta)] text-white grid place-items-center shadow" data-testid="sumup-proof-remove">
                  <span className="text-xs">×</span>
                </button>
              </div>
            ) : (
              <label className="block rounded-xl border-2 border-dashed border-[var(--border-warm)] bg-white hover:border-[var(--navy)] p-4 text-center cursor-pointer transition-colors" data-testid="sumup-proof-dropzone">
                <input type="file" accept="image/*" className="hidden" onChange={(e)=> uploadProof(e.target.files?.[0])} data-testid="sumup-proof-input"/>
                <div className="text-sm font-semibold">{proofUploading ? "Uploading…" : "Choose a photo"}</div>
                <div className="text-[11px] text-zinc-500 mt-1">PNG · JPG · WEBP - up to 8 MB</div>
              </label>
            )}

            <div className="flex gap-2 pt-2">
              <button type="button" className="chip flex-1" onClick={()=>setSumUpStep(null)} data-testid="sumup-cancel-btn">Cancel</button>
              <button className="btn-terracotta flex-1 py-2 rounded-full text-sm font-semibold disabled:opacity-70" disabled={loading || proofUploading} data-testid="sumup-confirm-btn">
                {loading ? "Submitting…" : "Confirm payment"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

const downloadPdf = async (path, fname) => {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Download failed"); }
};

function ExamOutcomeBanner({ course, outcome, onRepay }) {
  if (outcome?.final_status === "passed") {
    return (
      <div className="card-warm p-5 mb-4 border-l-4 border-emerald-500" data-testid="exam-outcome-passed">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800" data-testid="exam-final-status">
            <Award size={13}/> Completed · PASS (final)
          </span>
          {outcome.best_percent != null && <span className="text-sm text-zinc-700">Best score: <b data-testid="exam-best-score">{outcome.best_percent}%</b></span>}
        </div>
        <p className="text-sm text-zinc-700 mt-3">
          Your certificate will be sent by the CSI Administrator by separate email within 24 hours - please also check your spam / junk folders.
          {outcome.can_resit && " You still have a resit available if you wish to improve your grade - your pass stays final whatever the result."}
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => downloadPdf(`/courses/${course.id}/my-results-report`, `csi-results-report-${course.code || "exam"}.pdf`)} className="btn-terracotta px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="download-results-report-btn">
            <FileText size={14}/> Download results report
          </button>
          <button onClick={() => downloadPdf(`/courses/${course.id}/my-transcript`, `csi-transcript-${course.code || "exam"}.pdf`)} className="chip inline-flex items-center gap-2 text-sm" data-testid="download-transcript-btn">
            <FileText size={14}/> Download transcript
          </button>
        </div>
      </div>
    );
  }
  if (outcome?.needs_repayment) {
    return (
      <div className="card-warm p-5 mb-4 border-l-4 border-red-500" data-testid="exam-outcome-failed">
        <div className="font-semibold text-[var(--navy)]">All {outcome.max_attempts} attempts were not successful</div>
        <p className="text-sm text-zinc-700 mt-2">To resit, please pay for the exam again. All your previous attempts and records are kept.</p>
        <button onClick={onRepay} className="btn-terracotta px-4 py-2 rounded-full text-sm font-semibold mt-4" data-testid="repay-exam-btn">Pay to resit exam</button>
      </div>
    );
  }
  return null;
}

function StudyGuidePreview({ courseId }) {
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    api.get(`/courses/${courseId}/study-guides-preview`).then((r) => setGuides(r.data)).catch(() => setGuides([]));
  }, [courseId]);
  if (!guides.length) return null;
  return (
    <div className="mt-5 rounded-2xl bg-white/5 p-4" data-testid="study-guide-preview">
      <div className="eyebrow text-[var(--gold-soft)]">Included study materials</div>
      {guides.map((g) => (
        <div key={g.code} className="mt-2">
          <div className="font-semibold text-sm">{g.title}</div>
          <div className="text-xs text-zinc-300">PDF · {g.pages} pages · Aligned with CSF-STRAT and the <a href="https://gcsb.uk/standards" target="_blank" rel="noopener noreferrer" className="underline">GCSB standards (Version 2.0, effective 13 October 2026)</a> · Unlocks with your exam after enrolment</div>
          <details className="mt-2 group" data-testid={`study-guide-contents-${g.code}`}>
            <summary className="cursor-pointer text-xs font-semibold text-[var(--gold-soft)]">View contents</summary>
            <ol className="mt-2 space-y-1 text-xs text-zinc-200">
              {g.chapters.map((c) => <li key={c} className="flex gap-2"><Lock size={11} className="mt-0.5 shrink-0 text-zinc-400"/><span>{c}</span></li>)}
            </ol>
          </details>
        </div>
      ))}
    </div>
  );
}

function StudyGuides({ courseId }) {
  const [guides, setGuides] = useState([]);
  useEffect(() => {
    api.get(`/courses/${courseId}/study-guides`).then((r) => setGuides(r.data)).catch(() => setGuides([]));
  }, [courseId]);
  if (!guides.length) return null;
  return (
    <div className="mt-5 rounded-2xl bg-white/5 p-4" data-testid="study-guides">
      <div className="eyebrow text-[var(--gold-soft)]">Study materials</div>
      <ul className="mt-2 space-y-2">
        {guides.map((g) => (
          <li key={g.code} className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold text-sm">{g.title}</div>
              <div className="text-xs text-zinc-300">PDF{g.pages ? ` · ${g.pages} pages` : ""} · Aligned with CSF-STRAT and the <a href="https://gcsb.uk/standards" target="_blank" rel="noopener noreferrer" className="underline">GCSB standards (Version 2.0, effective 13 October 2026)</a></div>
            </div>
            <button onClick={() => downloadPdf(`/courses/${courseId}/study-guides/${g.code}`, `CSI-${g.code}-Study-Guide.pdf`)} className="chip bg-white/10 border-white/20 text-white text-xs inline-flex items-center gap-1" data-testid={`study-guide-download-${g.code}`}>
              <FileText size={13}/> Download
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Course Action Panel ---------- */
function CourseActionPanel({ course, enrolled, user, onRepay }) {
  const [sessions, setSessions] = useState([]);
  const [benchmark, setBenchmark] = useState(null);
  const [examSummary, setExamSummary] = useState(null);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get(`/exams/course/${course.id}/summary`).then((r) => setExamSummary(r.data)).catch(() => {});
    // Fetch upcoming live sessions scoped to this course if the course offers live cohorts
    if (course.is_live) {
      api.get("/live-sessions").then((r) => {
        const now = new Date();
        setSessions((r.data || []).filter((s) => (s.course_id === course.id || s.course_code === course.code) && new Date(s.scheduled_at) > now).slice(0, 5));
      }).catch(() => {});
    }
    if (enrolled && user) {
      api.get(`/courses/${course.id}/benchmark`).then((r) => setBenchmark(r.data)).catch(() => {});
    }
  }, [course.id, course.code, course.is_live, enrolled, user]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please log in to contact your instructor."); return; }
    if (!subject.trim() || !message.trim()) { toast.error("Subject and message are required."); return; }
    setSending(true);
    try {
      await api.post("/enquiries", { course_id: course.id, subject: subject.trim(), message: message.trim() });
      toast.success("Enquiry sent - a reply will land in your Dashboard.");
      setSubject(""); setMessage(""); setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send");
    } finally { setSending(false); }
  };

  return (
    <>
      {/* Launch exam / live sessions block */}
      <section className="mt-10 rounded-3xl bg-[var(--navy)] text-white p-6 sm:p-8" data-testid="course-launch-panel">
        <div className="eyebrow text-[var(--gold-soft)]">{course.is_live ? "Programme access" : "Partner exam portal"}</div>
        <h2 className="font-display text-2xl sm:text-3xl mt-1">
          {course.is_live ? "Live cohort access + online examination" : course.delivery_type === "direct_assessment" ? "Chartered direct assessment" : "Sit the exam online"}
        </h2>
        <p className="text-zinc-300 mt-2 max-w-2xl text-sm">
          {course.is_live
            ? "Join your scheduled cohort sessions and sit the governed CSI exam when you're ready."
            : course.delivery_type === "direct_assessment"
              ? "Direct chartered assessment for experienced leaders. Panel review via the CSI exam engine; portfolio uploads from your dashboard."
              : "This programme is delivered as an examination only. After payment approval, click Launch exam to begin."}
        </p>
        <div className="flex flex-wrap gap-3 mt-5">
          {enrolled || isAdmin ? (
            <Link to={`/exams/take/${course.id}`} className="btn-terracotta inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold" data-testid="launch-exam-btn">
              <ExternalLink size={16}/> {isAdmin && !enrolled ? "Preview exam (admin)" : "Launch exam"}
            </Link>
          ) : (
            <div className="text-sm text-zinc-400">Complete your enrolment on the right to unlock the exam.</div>
          )}
          {course.is_live && (
            <Link to="/live" className="chip bg-white/10 border-white/20 text-white inline-flex items-center gap-2 text-sm" data-testid="all-live-sessions-link">
              <Radio size={14}/> See all live sessions
            </Link>
          )}
        </div>

        {examSummary?.has_exam && (
          <div className="mt-5 rounded-2xl bg-white/5 p-4" data-testid="exam-preview-card">
            <div className="eyebrow text-[var(--gold-soft)]">Assessment preview</div>
            <div className="font-display text-lg mt-1">{examSummary.title}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-wider text-zinc-300">Questions</div><div className="font-mono text-base text-white">{(examSummary.question_counts?.mcq || 0) + (examSummary.question_counts?.tf || 0) + (examSummary.question_counts?.essay || 0)}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-zinc-300">Total points</div><div className="font-mono text-base text-white">{examSummary.total_points}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-zinc-300">Time limit</div><div className="font-mono text-base text-white" data-testid="exam-preview-time">{examSummary.time_limit_minutes ? (examSummary.time_limit_minutes % 60 === 0 ? `${examSummary.time_limit_minutes / 60} hours` : `${examSummary.time_limit_minutes} min`) : "Untimed"}</div></div>
              <div><div className="text-[10px] uppercase tracking-wider text-zinc-300">Pass mark</div><div className="font-mono text-base text-white">{examSummary.pass_mark_percent != null ? `${examSummary.pass_mark_percent}%` : "Examiner-graded"}</div></div>
            </div>
            <div className="text-[11px] text-zinc-300 mt-2">
              {examSummary.question_counts?.mcq || 0} multiple-choice · {examSummary.question_counts?.tf || 0} true-false · {examSummary.question_counts?.essay || 0} essay · up to {examSummary.max_attempts} attempt{examSummary.max_attempts === 1 ? "" : "s"}
            </div>
          </div>
        )}

        {(enrolled || isAdmin) ? <StudyGuides courseId={course.id}/> : <StudyGuidePreview courseId={course.id}/>}

        {course.is_live && sessions.length > 0 && (
          <div className="mt-6" data-testid="course-upcoming-sessions">
            <div className="eyebrow text-[var(--gold-soft)] mb-2">Upcoming live sessions</div>
            <ul className="space-y-2">
              {sessions.map((s) => (
                <li key={s.id} className="rounded-xl bg-white/5 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`session-${s.id}`}>
                  <div>
                    <div className="font-semibold text-sm">{s.title}</div>
                    <div className="text-xs text-zinc-300">
                      {new Date(s.scheduled_at).toLocaleString()} · {s.duration_min || 60} min
                      {s.rsvp_count ? ` · ${s.rsvp_count} attending` : ""}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {user && (
                      <button
                        onClick={async () => {
                          try {
                            if (s.my_rsvp) {
                              const { data } = await api.delete(`/live-sessions/${s.id}/rsvp`);
                              toast.success("RSVP cancelled");
                              setSessions((prev) => prev.map((x) => x.id === s.id ? { ...x, my_rsvp: false, rsvp_count: data.rsvp_count } : x));
                            } else {
                              const { data } = await api.post(`/live-sessions/${s.id}/rsvp`);
                              toast.success("See you there - RSVP noted");
                              setSessions((prev) => prev.map((x) => x.id === s.id ? { ...x, my_rsvp: true, rsvp_count: data.rsvp_count } : x));
                            }
                          } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                        }}
                        className={`chip text-xs inline-flex items-center gap-1 border-white/20 ${s.my_rsvp ? "bg-emerald-500/20 text-emerald-200" : "bg-white/10 text-white"}`}
                        data-testid={`rsvp-${s.id}`}
                      >
                        {s.my_rsvp ? "✓ I'm attending" : "I'll attend"}
                      </button>
                    )}
                    {enrolled && s.zoom_url && (
                      <a href={s.zoom_url} target="_blank" rel="noreferrer" className="chip bg-[var(--terracotta)] text-white border-[var(--terracotta)] text-xs inline-flex items-center gap-1" data-testid={`join-zoom-${s.id}`}>
                        <ExternalLink size={12}/> Join Zoom
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Benchmark + results */}
      {enrolled && (
        <section className="mt-10" data-testid="course-benchmark-section">
          <div className="eyebrow inline-flex items-center gap-1 text-[var(--gold-deep)]"><BarChart3 size={13}/> Benchmark & results</div>
          <h2 className="font-display text-2xl mt-1 mb-4">Your performance</h2>
          <ExamOutcomeBanner course={course} outcome={benchmark?.outcome} onRepay={onRepay}/>
          {!benchmark ? (
            <div className="text-sm text-zinc-500">Loading your results…</div>
          ) : benchmark.my_attempts.length === 0 ? (
            <div className="text-sm text-zinc-600 card-warm p-5">You haven't sat this exam yet. Click <b>Launch exam</b> above to begin.</div>
          ) : (
            <div className="grid lg:grid-cols-[1fr_1fr] gap-4">
              <div className="card-warm p-5" data-testid="my-attempts-list">
                <div className="eyebrow mb-2">My attempts</div>
                <ul className="divide-y divide-[var(--border-warm)]">
                  {benchmark.my_attempts.map((a) => (
                    <li key={a.id} className="py-3 text-sm">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <div className="font-mono text-base">{a.percent != null ? `${a.percent}%` : "-"} <span className="text-zinc-500 text-xs">({a.auto_score}/{a.auto_total} auto)</span></div>
                          <div className="text-[11px] text-zinc-500">{(a.submitted_at || a.started_at || "").slice(0, 16).replace("T", " ")}</div>
                        </div>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          a.status === "passed" ? "bg-emerald-100 text-emerald-800" :
                          a.status === "failed" ? "bg-red-100 text-red-800" :
                          a.status === "graded" ? "bg-zinc-200 text-zinc-700" :
                          "bg-blue-100 text-blue-800"
                        }`} data-testid={`attempt-status-${a.id}`}>{a.status.replace("_", " ")}</span>
                      </div>
                      {Object.keys(a.manual_feedback || {}).length > 0 && (
                        <details className="mt-2">
                          <summary className="text-xs text-[var(--terracotta)] cursor-pointer">Examiner feedback</summary>
                          <div className="mt-2 space-y-1">
                            {Object.entries(a.manual_feedback).map(([qid, v]) => (
                              <div key={qid} className="text-xs text-zinc-700 bg-[var(--muted-surface)] rounded-lg p-2">
                                <b>Q{qid.slice(0, 6)}:</b> {v.points} pts - {v.feedback || "no comment"}
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-warm p-5" data-testid="cohort-benchmark">
                <div className="eyebrow mb-2 inline-flex items-center gap-1"><TrendingUp size={12}/> Cohort benchmark</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat label="Pass rate" value={benchmark.cohort.pass_rate != null ? `${benchmark.cohort.pass_rate}%` : "-"}/>
                  <Stat label="Cohort mean" value={benchmark.cohort.mean_percent != null ? `${benchmark.cohort.mean_percent}%` : "-"}/>
                  <Stat label="Cohort median" value={benchmark.cohort.median_percent != null ? `${benchmark.cohort.median_percent}%` : "-"}/>
                </div>
                {benchmark.my_attempts.length > 0 && benchmark.cohort.median_percent != null && benchmark.my_attempts[0].percent != null && (
                  <div className="mt-4 text-xs text-zinc-600" data-testid="benchmark-verdict">
                    Your latest score of <b>{benchmark.my_attempts[0].percent}%</b> is {benchmark.my_attempts[0].percent >= benchmark.cohort.median_percent ? "at or above" : "below"} the cohort median.
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {/* Contact instructor + admin */}
      <section className="mt-10" data-testid="course-contact-section">
        <div className="eyebrow inline-flex items-center gap-1 text-[var(--gold-deep)]"><MessageCircle size={13}/> Contact instructor &amp; admin</div>
        <h2 className="font-display text-2xl mt-1 mb-4">Send an enquiry</h2>
        {!user ? (
          <div className="card-warm p-5 text-sm text-zinc-600">Please <Link to="/login" className="text-[var(--terracotta)] font-semibold">log in</Link> to send an enquiry about this programme.</div>
        ) : sent ? (
          <div className="card-warm p-5 text-sm text-zinc-700">
            Thanks - your enquiry is on its way. Replies will appear in your <Link to="/dashboard" className="text-[var(--terracotta)] font-semibold">Dashboard → My enquiries</Link>.
            <button onClick={() => setSent(false)} className="block mt-2 chip text-xs">Send another</button>
          </div>
        ) : (
          <form onSubmit={submitEnquiry} className="card-warm p-5 space-y-3" data-testid="enquiry-form">
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject (e.g. Payment confirmation, Exam timing, Materials access)"
              className="w-full p-2 border border-[var(--border-warm)] rounded-lg text-sm"
              required
              data-testid="enquiry-subject"
            />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your question here. The course instructor and CSI admin will both receive your enquiry."
              rows={5}
              className="w-full p-2 border border-[var(--border-warm)] rounded-lg text-sm"
              required
              data-testid="enquiry-message"
            />
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-[11px] text-zinc-500">Your reply will arrive in your Dashboard; we'll also email <b>info@convergedsecurity.es</b>.</p>
              <button type="submit" disabled={sending} className="btn-terracotta inline-flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold" data-testid="enquiry-submit">
                <Send size={13}/> {sending ? "Sending…" : "Send enquiry"}
              </button>
            </div>
          </form>
        )}
      </section>
    </>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl bg-[var(--muted-surface)] p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="font-display text-2xl text-[var(--navy)]">{value}</div>
    </div>
  );
}
